import { useState } from 'react';
import { useQuery } from 'react-query';
import Select from 'react-select';
import {
  FormControl,
  FormLabel,
  useColorMode,
  useTheme,
} from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons';
import { apiService } from '../../../../services/ApiService';
import { useDebouncedCallback } from 'use-debounce';

const LocationInput = ({
  location,
  onChange,
  isValid,
  onRemove,
  isDisabled,
  hasPermissions = true,
  mt,
  mb,
  display,
  id,
  label,
}) => {
  const [query, setQuery] = useState('');
  const { colorMode } = useColorMode();
  const theme = useTheme();
  const [isFocused, setIsFocused] = useState(false);

  const isFloating = Boolean(location?.label) || isFocused;
  const { data: locations } = useQuery({
    queryKey: ['Locations', query],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      if (!query) {
        return [];
      }
      const response = await apiService.getLocations({
        search: query,
        country: 'au',
      });
      return response.data;
    },
  });

  const locationOptions =
    locations?.map((loc) => ({
      label: loc.name,
      value: loc.placeId || null,
      lat: loc.lat || null,
      lon: loc.long || null,
    })) || [];

  const handleInputChange = useDebouncedCallback((inputValue) => {
    setQuery(inputValue);
  }, 500);

  const handleSelectChange = (selectedOption) => {
    if (selectedOption) {
      console.log('selectedOption', selectedOption);
      onChange({
        ...selectedOption,
        state: null,
        lat: selectedOption.lat,
        lon: selectedOption.lon,
      });
      setQuery(selectedOption.label);
    }
  };

  const customStyles = {
    singleValue: (provided) => ({
      ...provided,
      color: 'white',
    }),
    control: (provided, state) => ({
      ...provided,
      backgroundColor: 'transparent',
      color: 'white',
      borderColor:
        colorMode === 'dark' ? theme.colors.gray[600] : theme.colors.gray[300],
      boxShadow: 'none',
      height: '54px',
      minHeight: '54px',
      padding: '0 9px 0 16px !important',
      display: 'flex',
      alignItems: 'center',
      borderRadius: '10px',
      border: '1px solid #9FABCB',
      fontSize: '16px',
      fontWeight: '400',
      transition: '0.3s all !important',
      '& > div:first-child': {
        paddingLeft: '0',
      },
      '& input': {
        padding: '0 !important',
        borderRadius: '0 !important',
        border: 'none !important',
        fontSize: 'inherit !important',
        fontWeight: 'inherit !important',
        transition: 'none !important',
        color: 'white !important',
      },
      '& input:focus': {
        boxShadow: 'none !important',
      },

      ...(!state.menuIsOpen && {
        '&:hover': {
          boxShadow: '0 0 0 0.5px #099BFF !important;',
          borderColor: '#9FABCB !important',
        },
      }),

      ...(state.menuIsOpen && {
        boxShadow: '0 0 0 0.5px #099BFF !important',
        borderColor: '#099BFF !important',
      }),
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: '#0D234D',
      color: 'white',
      zIndex: 100,
      borderRadius: '10px',
      borderWidth: '1px',
      borderColor: '#262D47',
      boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? '#29498F'
        : state.isFocused
        ? '#10337F'
        : '',
      color: 'white',
      padding: '10px 24px',
      fontSize: '14px',
      fontWeight: '400',
      '&:hover': {
        backgroundColor: '#10337F !important',
      },
      '&:active': {
        backgroundColor: '#10337F !important',
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9FABCB',
      margin: '0',
    }),
    indicatorContainer: (provided) => ({
      ...provided,
      width: '1.5rem',
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }),
  };

  return (
    <FormControl
      variant="floating"
      id={id}
      mt={mt}
      mb={mb}
      display={display}
      isInvalid={!isValid || !hasPermissions}
    >
      <Select
        inputId={id}
        isDisabled={isDisabled || !hasPermissions}
        className="react-select"
        value={location || null}
        onInputChange={handleInputChange}
        onChange={handleSelectChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        options={locationOptions}
        placeholder="Enter location"
        isSearchable
        isLoading={!locations}
        styles={customStyles}
        components={{
          DropdownIndicator: () =>
            location?.label ? (
              <DeleteIcon onClick={onRemove} color="#9FABCB" boxSize={5} />
            ) : null,
          LoadingIndicator: null,
          IndicatorSeparator: null,
        }}
      />

      <FormLabel
        htmlFor={id}
        className={isValid ? 'input-valid' : 'input-invalid'}
        sx={
          isFloating
            ? {
                fontSize: '14px',
                transform: 'scale(0.85) translateY(-28px)',
                color: isFocused ? '#099BFF' : '#9FABCB',
              }
            : undefined
        }
      >
        {label}
      </FormLabel>
      {(!isValid || !hasPermissions) && (
        <div className="input-error">
          {!hasPermissions
            ? 'You do not have sufficient permissions to use this input. Please contact an administrator to request access.'
            : 'Input invalid'}
        </div>
      )}
    </FormControl>
  );
};

export default LocationInput;
